import React from 'react';
import styles from '../styles/wizards/ExportPlanAnotationsWizard.scss';
import ExportIcon from '../assets/images/export-15x15.svg';
import { __ } from '../core/utils';
import { setNotification } from '../redux/actions/general/notification';
import { withRouter } from 'react-router';
import ExportPlanAnnotationsForm from '../forms/export_plan_annotations_wizard/ExportPlanAnnotationsForm';
import { connect } from 'react-redux';
import {
  downloadPlanVersion,
  exportPlanVersion,
} from '../redux/actions/table/working_set_plans';
import {
  downloadPlanRevision,
  exportPlanRevision,
} from '../redux/actions/table/current_set_plans';

const ExportPlanAnnotationsWizard = ({
  onClose,
  table: { flyout },
  is_revision_mode,
  canvas_editor: { fabricOverlay },
}) => {
  const width = flyout.width;
  const height = flyout.height;

  const exportSVGForType = (fabricCanvas, type) => {
    const typeMap = {
      lines: 'line',
      ellipses: 'ellipse',
      rectangles: 'rectangle',
      polygons: 'polygon',
      freehands: 'freehand',
      clouds: 'cloud',
      tasks: 'task',
      specifications: 'specification',
      plans: 'plan',
    };

    const singularType = typeMap[type];
    if (!singularType) return Promise.resolve(null);

    const filteredObjects = fabricCanvas
      .getObjects()
      .filter((obj) => obj.objectType === singularType);

    if (filteredObjects.length === 0) return Promise.resolve(null);

    const tempCanvas = new fabric.Canvas(null, {
      width,
      height,
    });

    return Promise.all(
      filteredObjects.map(
        (obj) =>
          new Promise((resolve) => {
            obj.clone((cloned) => resolve(cloned));
          })
      )
    ).then((clones) => {
      clones.forEach((clone) => tempCanvas.add(clone));
      tempCanvas.renderAll();

      const svg = tempCanvas.toSVG({ viewBox: `0 0 ${width} ${height}` });

      tempCanvas.dispose();
      return svg;
    });
  };

  const _handleSubmit = (formData) => {
    const target_id = is_revision_mode
      ? _.last(flyout.revisions)?.id
      : _.last(flyout.versions)?.id;
    if (!target_id) {
      onClose();
      return;
    }

    if (!formData.annotations) {
      onClose();

      setNotification(__('plans.download-plan-version'));

      return setTimeout(() => {
        if (is_revision_mode) {
          return downloadPlanRevision(target_id);
        }

        return downloadPlanVersion(target_id);
      }, 500);
    }

    const canvas = fabricOverlay.fabricCanvas();

    const exportedSVGs = {};

    const promises = Object.entries(formData)
      .filter(([type]) => formData[type])
      .map(([type]) =>
        exportSVGForType(canvas, type).then((svg) => {
          if (svg) exportedSVGs[type] = svg;
        })
      );

    Promise.all(promises).then(() => {
      if (is_revision_mode) {
        exportPlanRevision(target_id, { svgs: exportedSVGs });
      } else {
        exportPlanVersion(target_id, { svgs: exportedSVGs });
      }

      onClose();

      setNotification(__('plans.export-plan-version'));
    });
  };

  return (
    <>
      <div className={styles.header}>
        <div className={styles.left}>
          <span>{__('plans.export-plan-annotations')}</span>
          <span>{__('plans.choose-options-export-plan-annotations')}</span>
        </div>
        <div className={styles.right}>
          <ExportIcon />
        </div>
      </div>
      <div className={styles.body}>
        <ExportPlanAnnotationsForm
          onSubmit={_handleSubmit}
          initialValues={{
            lines: true,
            ellipses: true,
            rectangles: true,
            polygons: true,
            freehands: true,
            clouds: true,
            tasks: true,
            specifications: true,
            plans: true,
          }}
        />
      </div>
    </>
  );
};

const mapStateToProps = (state, props) => {
  const table_key = props.is_revision_mode
    ? 'current_set_plans'
    : 'working_set_plans';

  return {
    canvas_editor: state.canvas_editor,
    table: state.table[table_key],
  };
};

export default withRouter(
  connect(mapStateToProps, null)(ExportPlanAnnotationsWizard)
);
